import {
  combineSlices,
  configureStore,
  createAction,
  UnknownAction,
} from '@reduxjs/toolkit'
import { api } from '@wellesley/openapi'
import { authSlice } from '@wellesley/auth/authSlice'
import { userSlice } from '@wellesley/user/slices/userSlice'
import { initialStateSlice } from '@wellesley/openapi/slices/initialStateSlice'
import { subscribeActionMiddleware } from '@wellesley/redux-subscribe-action'
import { instanceInfoSlice } from '@wellesley/instanceinfo/slice'
import { profileSettingsSlice } from '@wellesley/settings/slices/profileSettingsSlice'
import { platformSettingsSlice } from '@wellesley/settings/slices/platformSettingsSlice'
import { notificationsSlice } from '@wellesley/notifications/slice'
import { uiSlice } from '@wellesley/ui/slices/uiSlice'
import { networkSlice } from '@wellesley/network/slices/networkSlice'

declare global {
  interface Window {
    _wlsy_store: AppStore
  }
}

export const rootReducer = combineSlices(
  api,
  networkSlice,
  uiSlice,
  authSlice,
  userSlice,
  initialStateSlice,
  instanceInfoSlice,
  notificationsSlice,
  profileSettingsSlice,
  platformSettingsSlice,
)

export const hydrate = createAction<Partial<RootState>>('app/hydrate')

export const rootReducerWithHydrate = (
  state: RootState | undefined,
  action: unknown,
) => {
  if (hydrate.match(action)) return { ...state, ...action.payload }
  return rootReducer(state, action as UnknownAction)
}

export const createStore = <T>(preloadedState?: Partial<T & RootState>) => {
  const newStore = configureStore({
    devTools: {
      name: process.env.APP_NAME || 'wellesley',
    },
    reducer: rootReducerWithHydrate,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(api.middleware, subscribeActionMiddleware),
  })

  window._wlsy_store = newStore

  return newStore
}

export const getStore = () => window._wlsy_store

export const getOrCreateStore = (preloadedState?: Partial<RootState>) => {
  if (window._wlsy_store) {
    return window._wlsy_store
  }

  window._wlsy_store = createStore(preloadedState)
  return window._wlsy_store
}

export type RootState = ReturnType<typeof rootReducer>
export type AppStore = ReturnType<typeof createStore>
export type AppDispatch = typeof window._wlsy_store.dispatch
